import React from "react";
import PropTypes from "prop-types";

import { cossAppendicesForLine } from "../../Helpers/cossLineDetails";
import { BooleanToggle, DefinitionGrid, DetailGroup, formatDistanceUnit, formatSiteDate, Signature } from "./PackDetailPrimitives";

const distance = (value, unit) => value?.[unit] || value?.milesYards || value?.milesChains || value?.kilometersMeters;
const date = (value) => formatSiteDate(value) || value;

const PackRt3199Details = ({ conversionUnit, handovers = [], line, location, possessions, users }) => {
  const record = line.rt3199 || {};
  const distanceUnitLabel = formatDistanceUnit(conversionUnit);
  const crossings = possessions.flatMap((possession) => possession.level_crossings || []).filter((crossing) => (
    (crossing.lines || []).some((crossingLine) => crossingLine.line_reference === line.line_reference)
  ));
  const appendices = cossAppendicesForLine(users, line.line_reference);

  return <div className="wsm-pack-detail__stack">
    <DefinitionGrid items={[
      { label: "Line affected", value: line.name }, { label: "Engineering Supervisor", value: record.es_swl_name || (users.esusers || []).find((user) => user.user_id === record.es_swl_id)?.name },
      { label: "Employer", value: record.employer }, { label: "WON item number", value: location?.won_item_no },
      { label: `Possession start (${distanceUnitLabel})`, value: distance(line.poss_start, conversionUnit) }, { label: `Possession end (${distanceUnitLabel})`, value: distance(line.poss_end, conversionUnit) },
      { label: `Worksite start (${distanceUnitLabel})`, value: distance(line.start, conversionUnit) }, { label: `Worksite end (${distanceUnitLabel})`, value: distance(line.end, conversionUnit) },
    ]} />

    <DetailGroup title="Level crossings">
      {crossings.length === 0 ? <p className="wsm-pack-detail__empty">No level crossings are associated with this line.</p> : crossings.map((crossing, index) => (
        <DefinitionGrid key={crossing.id || index} items={[
          { label: "Level crossing", value: crossing.level_crossing }, { label: "Operational status", value: crossing.operational_status },
          { label: "Arranged", value: date(crossing.date_arranged) }, { label: "Withdrawn", value: date(crossing.date_withdrawn) },
        ]} />
      ))}
    </DetailGroup>

    <DetailGroup title="Setting up the worksite"><DefinitionGrid items={[
      { label: "PICOP authority to set up worksite", value: date(record.authority_given_by_picop_setup) }, { label: "PICOP phone number", value: record.picop_phone_no },
      { label: "PICOP advised worksite set up", value: date(record.worksite_set_up_picop_advised) }, { label: "Authority to allow work to start", value: date(record.authority_given_by_picop_start_work) },
      { label: "PICOP name", value: record.picop_name },
      { content: <BooleanToggle label="Signed in by telephone" value={record.picop_signed_in_by_telephone} />, label: "Signed in by telephone" },
    ]} /></DetailGroup>

    <DetailGroup title="Change of Engineering Supervisor">
      {handovers.length === 0 ? <p className="wsm-pack-detail__empty">No handovers recorded.</p> : handovers.map((handover, index) => <DefinitionGrid key={handover.id || index} items={[
        { label: "Name of new ES", value: handover.name_of_es }, { label: "Employer", value: handover.employer },
        { label: "Changeover at", value: date(handover.change_over_at) }, { label: "PICOP advised at", value: date(handover.picop_advised_at) },
        { label: "Comments", value: handover.comments },
      ]} />)}
    </DetailGroup>

    <DetailGroup title="Marker boards">
      {(record.marker_board_placed || []).length === 0 && (record.marker_board_lifted || []).length === 0
        ? <p className="wsm-pack-detail__empty">No marker board placement recorded.</p>
        : <div className="wsm-rt3199__marker-boards">
          {(record.marker_board_placed || []).length > 0 && <DefinitionGrid items={record.marker_board_placed.map((board, index) => (
            { label: `${board.name || `Board ${index + 1}`} placed`, value: `${board.placed ? "Yes" : "No"}${date(board.marker_datetime) ? ` \u2014 ${date(board.marker_datetime)}` : ""}` }
          ))} />}
          {(record.marker_board_lifted || []).length > 0 && <DefinitionGrid items={record.marker_board_lifted.map((board, index) => (
            { label: `${board.name || `Board ${index + 1}`} lifted`, value: `${board.lifted ? "Yes" : "No"}${date(board.marker_datetime) ? ` \u2014 ${date(board.marker_datetime)}` : ""}` }
          ))} />}
        </div>}
    </DetailGroup>

    <DetailGroup title="Giving up the worksite"><DefinitionGrid items={[
      { label: "Permission requested to remove WSMBs", value: date(record.picop_permission_requested_remove_wsmb) },
      { label: "Work completed and line safe", value: date(record.work_completed) }, { label: "Certificate handed to PICOP / PICOP advised", value: date(record.certificate_handed_picop_advised) },
      { label: "PICOP authority to give up worksite", value: date(record.authority_given_by_picop_give_up_work) },
      { label: "Worksite given up with train at signal", value: date(record.worksite_given_up_with_train) }, { label: "Train", value: record.train }, { label: "Signal", value: record.signal },
    ]} /><Signature label="Signature of ES" value={record.es_signature} /></DetailGroup>

    <DetailGroup title="Appendix A - IWA/COSS details">
      {appendices.length === 0 ? <p className="wsm-pack-detail__empty">No IWA/COSS details attached to this line.</p> : appendices.map((user, index) => <section className="wsm-pack-detail__subrecord" key={user.user_id || index}>
        <DefinitionGrid items={[
          { label: "Sign-in name", value: user.coss_name_sign_in || user.name }, { label: "Phone number", value: user.phone_no }, { label: "Employer", value: user.employer },
          { label: "Limits of site of work", value: user.limits_site_of_work }, { label: "Nature of work", value: user.nature_of_work_undertaken }, { label: "Safe system of work", value: user.ssow_in_use },
          { label: "Authority to start reference", value: user.authority_to_start_iwa_coss_number }, { label: "Authority to start", value: date(user.authority_to_start_iwa_coss_datetime) },
          { label: "Work completed reference", value: user.iwa_coss_confirm_completed_number }, { label: "Work completed", value: date(user.iwa_coss_confirm_completed_datetime) },
          { label: "Sign-out name", value: user.coss_name_sign_out },
        ]} />
        <div className="wsm-pack-detail__signatures"><Signature label="Sign-in signature" value={user.coss_signature_sign_in} /><Signature label="Sign-out signature" value={user.coss_signature_sign_out} /></div>
      </section>)}
    </DetailGroup>
  </div>;
};

PackRt3199Details.propTypes = {
  conversionUnit: PropTypes.string.isRequired,
  handovers: PropTypes.arrayOf(PropTypes.object),
  line: PropTypes.object.isRequired,
  location: PropTypes.object,
  possessions: PropTypes.arrayOf(PropTypes.object).isRequired,
  users: PropTypes.object.isRequired,
};

export default PackRt3199Details;
